'use client';

import Icon from '@/components/ui/AppIcon';
import { useTheme } from './ThemeProvider';
import type { ThemePreference } from './ThemeProvider';

const themeOptions: Array<{
  value: ThemePreference;
  label: string;
  icon: string;
}> = [
  { value: 'system', label: 'Use system theme', icon: 'ComputerDesktopIcon' },
  { value: 'light', label: 'Use light theme', icon: 'SunIcon' },
  { value: 'dark', label: 'Use dark theme', icon: 'MoonIcon' },
];

export default function ThemeSwitcher({ className = '' }: { className?: string }) {
  const { preference, resolvedTheme, setPreference } = useTheme();

  return (
    <div
      className={`inline-flex items-center rounded-full border border-brand-border bg-brand-surface p-1 shadow-[0_8px_24px_rgba(15,23,42,0.06)] ${className}`}
      role="group"
      aria-label={`Theme switcher. Current theme: ${preference}, showing ${resolvedTheme}.`}
    >
      {themeOptions.map((option) => {
        const active = preference === option.value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => setPreference(option.value)}
            className={`flex h-8 w-8 items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
              active
                ? 'bg-white text-primary shadow-[0_4px_14px_rgba(15,23,42,0.12)]'
                : 'text-brand-muted hover:text-brand-dark'
            }`}
            aria-label={option.label}
            aria-pressed={active}
            title={option.label}
          >
            <Icon name={option.icon} size={16} variant={active ? 'solid' : 'outline'} />
          </button>
        );
      })}
    </div>
  );
}
